// Haftanın Temsilcisi — haftalık "en iyi temsilci" bonusunun puanlaması (görev #143 Vegas /
// #144 Prime, 2026-09-28; ağırlıklar 2026-10-22'de, sonra 2026-10-23'te sahibin kararıyla
// değişti — görev #274 Vegas / #275 Prime):
//
//   QMS puanı      = QMS ortalaması / 10 × 5                           → mutlak (10,0 QMS = 5,00)   %50
//   Başarı puanı   = başarı oranı ÷ haftanın EN İYİ başarı oranı × 4   → zirve 4,00                 %40
//   Yatırım puanı  = her TAM 100.000 TL yatırım için 0,20, en fazla 1,00 (500.000 TL)              %10
//   Kritik ceza    = her kritik hata için −0,10
//   SKOR (10 üzerinden) = QMS + başarı + yatırım − ceza, en az 0
//
// Yatırım puanı MUTLAK (pencerenin yatırım tutarı; 24s'te 12s, 48s'te 36s sütunu) — artık haftanın
// en iyisine oranlanmıyor; 199.999 TL = 0,20 (yalnız tam 100.000'ler sayılır). Başarı puanı GÖRELİ
// kalır — 48s puanı 24s'ten düşük olabilir. Yanıtlayan üye sayısı ve yatırım yapan üye SAYISI
// puanlanmaz (2026-10-23'te yanıt hedefi kalktı). Kritik hata sayısı QMS'in haftalık sayısı
// (karnenin KRİTİK HATA sütunu) — iki pencerede aynı.
//
// Skor dışı temsilciler (`SCORE_EXCLUDED_AGENTS`, sahip 2026-10-23): Haftanın Temsilcisi kartında
// HİÇ görünmez, karnede SKOR hücreleri BOŞ ve hep en altta. Sıralamaya girmedikleri için başarı
// puanının "haftanın en iyisi" paydasını da etkilemezler.
//
// Sahibin kararları: iki pencere de (24s ve 48s) AYRI sıralanır (karnenin iki tablosu da skorlu;
// Haftanın Temsilcisi kartı 24s); QMS değerlendirmesi olmayan temsilci 0 QMS puanı alır
// (sıralamada kalır, en fazla 5,00 alabilir — değerlendirilmeden bonus kazanılmaz). Sıralama
// gösterilen skora (2 ondalık) göre; aynı skor aynı sırayı paylaşır (1, 1, 3) — bonus bir
// beraberliği sessizce ada göre bozmasın.
//
// SAF modül (React yok, import yok) — test/agent-ranking.test.mjs esbuild ile yükleyip formülü pinler.

export const QMS_WEIGHT = 5;
export const SUCCESS_WEIGHT = 4;
/** WeeklyReport.tsx dipnotu bu üç sayıyı DÜZ METİN olarak taşır (§2), birlikte değiştir. */
export const DEPOSIT_STEP_TL = 100000;
export const DEPOSIT_STEP_POINTS = 0.2;
export const DEPOSIT_MAX_POINTS = 1;
export const CRITICAL_PENALTY = 0.1;

/** Marka → skor dışı temsilci adları (sheet'teki ad; TR büyük/küçük harf ve İ/I katlanarak TAM eşleşme). */
export const SCORE_EXCLUDED_AGENTS: Readonly<Record<string, readonly string[]>> = {
  vegas: ['HAVA'],
  prime: ['SEVİL', 'SEMRA'],
};

export interface RankInputAgent {
  name: string;
  deposit12h: number;
  deposit36h: number;
  successRate12hPct: number;
  successRate36hPct: number;
  qms?: { avgScore: number | null; evalCount: number; criticalCount?: number } | null;
}

export interface RankedAgent {
  rank: number;
  name: string;
  /** Ham girdiler — başlık (title) ipuçları için. `qmsScore` null = bu hafta değerlendirme yok. */
  qmsScore: number | null;
  qmsEvalCount: number;
  successRatePct: number;
  depositAmount: number;
  criticalCount: number;
  /** Bileşenler 10 üzerinden, tam hassasiyet; ceza pozitif sayı (düşülen). */
  qmsPoints: number;
  successPoints: number;
  depositPoints: number;
  criticalPenalty: number;
  /** 10 üzerinden, 2 ondalığa yuvarlı, en az 0 — gösterilen ve sıralanan değer. */
  score10: number;
}

const finite = (v: unknown): number => (typeof v === 'number' && Number.isFinite(v) ? v : 0);
const round2 = (v: number): number => Math.round(v * 100) / 100;

const foldName = (s: string): string =>
  String(s || '')
    .trim()
    .replace(/\s+/g, ' ')
    .toLocaleLowerCase('tr')
    .replace(/ı/g, 'i')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '');

export function isScoreExcluded(brand: string | null | undefined, name: string): boolean {
  const list = brand ? SCORE_EXCLUDED_AGENTS[brand] : undefined;
  if (!list || !list.length) return false;
  const n = foldName(name);
  return list.some((x) => foldName(x) === n);
}

/** Her TAM 100.000 TL için 0,20; en fazla 1,00. */
export function depositPointsFor(amount: number): number {
  const steps = Math.floor(Math.max(0, finite(amount)) / DEPOSIT_STEP_TL);
  return Math.min(DEPOSIT_MAX_POINTS, round2(steps * DEPOSIT_STEP_POINTS));
}

export function rankAgents(
  agents: RankInputAgent[] | null | undefined,
  window12: boolean,
  brand?: string | null,
): RankedAgent[] {
  const list = Array.isArray(agents) ? agents.filter((a) => !!a && !!a.name && !isScoreExcluded(brand, a.name)) : [];
  const rateOf = (a: RankInputAgent) => finite(window12 ? a.successRate12hPct : a.successRate36hPct);
  const depositOf = (a: RankInputAgent) => finite(window12 ? a.deposit12h : a.deposit36h);

  const bestRate = list.reduce((m, a) => Math.max(m, rateOf(a)), 0);

  const scored = list.map((a) => {
    const raw = a.qms && typeof a.qms.avgScore === 'number' && Number.isFinite(a.qms.avgScore) ? a.qms.avgScore : null;
    const qmsScore = raw == null ? null : Math.min(10, Math.max(0, raw));
    const criticalCount = a.qms ? Math.max(0, finite(a.qms.criticalCount)) : 0;
    const qmsPoints = qmsScore == null ? 0 : (qmsScore / 10) * QMS_WEIGHT;
    const successPoints = bestRate > 0 ? (Math.max(0, rateOf(a)) / bestRate) * SUCCESS_WEIGHT : 0;
    const depositPoints = depositPointsFor(depositOf(a));
    const criticalPenalty = round2(criticalCount * CRITICAL_PENALTY);
    const sum = Math.max(0, qmsPoints + successPoints + depositPoints - criticalPenalty);
    return {
      rank: 0,
      name: a.name,
      qmsScore,
      qmsEvalCount: a.qms ? finite(a.qms.evalCount) : 0,
      successRatePct: rateOf(a),
      depositAmount: depositOf(a),
      criticalCount,
      qmsPoints,
      successPoints,
      depositPoints,
      criticalPenalty,
      score10: round2(sum),
      sum,
    };
  });

  scored.sort((x, y) => y.score10 - x.score10 || y.sum - x.sum || x.name.localeCompare(y.name, 'tr'));
  scored.forEach((r, i) => {
    r.rank = i > 0 && r.score10 === scored[i - 1].score10 ? scored[i - 1].rank : i + 1;
  });
  return scored.map(({ sum: _sum, ...r }) => r);
}
